<template>
  <b-modal id="assign-study-modal" ref="assignStudiesModal" @hidden="resetValues" hide-footer :no-close-on-esc="noCloseOnBackdrop" :no-close-on-backdrop="noCloseOnBackdrop">
    <template slot="modal-title">{{ editingAssignment ? `Assigned Study – ${studyName}` : `Assign Study` }}</template>
    <div v-if="!editingAssignment" id="general-details" class="section">
      <div class="section-title">General Details</div>
      <div class="row no-gutters">
        <b-col cols="5">
          <div class="study-label">Study:</div>
          <studiesAutoCompleteDropDown :options="assignStudiesOptions" :selectedOption="selectedStudy" @input="studiesInputHandler($event)" />
        </b-col>
      </div>
    </div>
    <div v-if="selectedStudy" id="site-assignment-section" class="section">
      <div class="section-title">Site Assignment</div>
      <div id="assignments-section">
        <div v-if="hasAssignmentError" class="assignment-error"><span class="fa fa-exclamation-triangle" aria-hidden="true"></span>You must assign at least 1 site to the user</div>
        <div id="assignments">
          <div class="title">
            <div class="checkboxes-section">
              <label class="checkbox-label" data-testid="toggle-all-sites">
                <input type="checkbox" v-model="allSitesSelected" @change="toggleAllCountrySite($event.target.checked)" />
                <span class="input-checkbox" :class="{ intermidiate: isSomeSiteSelected }"></span>
                <span class="checkbox-text">
                  All Sites
                  <span v-if="allSelectedSitesCount" class="count">{{ allSelectedSitesCount }} of {{ allSitesCount }}</span>
                </span>
              </label>
            </div>
          </div>
          <div class="assign-county-site">
            <div v-if="isApiCallInProgress && !countries" class="loading">
              <span class="fa fa-spinner fa-spin"></span>
            </div>
            <div v-else>
              <div v-for="(countryWithSites, index) in countries" :key="`assignment-${countryWithSites.country.countryId}-${index}`" class="country-group">
                <div class="country-header">
                  <div class="title">
                    <div class="checkboxes-section">
                      <label class="checkbox-label">
                        <input type="checkbox" v-model="countryWithSites.isEnabled" @change="toggleCountrySiteSelection($event, countryWithSites)" :data-testid="`country-checkbox-${countryWithSites.country.countryId}-${index}`" />
                        <span class="input-checkbox" :class="{ intermidiate: countryWithSites.isSomeSiteSelected }"></span>
                        <span class="checkbox-text">
                          {{ countryWithSites.country.countryName }}
                          <span v-if="countryWithSites.selectedSitesCount" class="count">{{ countryWithSites.selectedSitesCount }} of {{ countryWithSites.sites.length }}</span>
                        </span>
                      </label>
                    </div>
                  </div>
                  <div class="checkboxes-section">
                    <span :class="{ disabled: countryWithSites.selectedSitesCount < countryWithSites.sites.length }">
                      <label class="switch">
                        <input type="checkbox" v-model="countryWithSites.includeFuture" />
                        <div class="slider round"></div>
                      </label>
                      <span class="checkbox-label"><span class="checkbox-text ml-8">Include future sites</span></span>
                    </span>
                    <div class="d-inline-block" @click="countryWithSites.expanded = !countryWithSites.expanded"><span class="fa" :class="countryWithSites.expanded ? 'fa-angle-up' : 'fa-angle-down'"></span></div>
                  </div>
                </div>
                <b-collapse :id="`assignment-data-${countryWithSites.country.countryId}-${index}`" v-model="countryWithSites.expanded">
                  <div v-for="(site, sindex) in countryWithSites.sites" :key="`site-${site.id}-${sindex}`" class="site-details">
                    <div class="checkboxes-section">
                      <label class="checkbox-label">
                        <input type="checkbox" v-model="site.isEnabled" @change="toggleSiteSelection(countryWithSites)" :data-testid="`site-checkbox-${countryWithSites.country.countryId}-${site.id}-${sindex}-${index}`" />
                        <span class="input-checkbox"></span>
                        <span class="checkbox-text">
                          {{ site.userSiteId }} - {{ site.name }}
                          <span v-if="site.sitePI"> ({{ site.sitePI.firstName }} {{ site.sitePI.lastName }})</span>
                        </span>
                      </label>
                    </div>
                  </div>
                </b-collapse>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <template #modal-footer>
      <div class="footer">
        <b-button class="btn-cancel" @click="hide()">
          Cancel
        </b-button>
        <b-button :disabled="disableSaveButton" variant="primary" @click="saveAssignments()" data-testid="save-assignments-btn">
          Save
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { v4 as uuidv4 } from "uuid";
import { cloneDeep } from "lodash";
import studiesAutoCompleteDropDown from "@/components/users/studies-auto-complete-dropdown";
export default {
  name: "assignStudiesModal",
  components: {
    studiesAutoCompleteDropDown
  },
  data() {
    return {
      noCloseOnBackdrop: true,
      selectedStudy: null,
      isApiCallInProgress: false,
      editingAssignment: false,
      sites: null,
      countries: null,
      allSitesCount: 0,
      allSitesSelected: false,
      isSomeSiteSelected: false,
      allSelectedSitesCount: 0,
      hasAssignmentError: false,
      isStudyAssociatedToUser: false,
      assignmentType: {
        ALL: "ALL",
        COUNTRY: "COUNTRY",
        SITE: "SITE"
      },
      assignStudiesOptions: []
    };
  },
  props: {
    userDetails: {
      type: Object,
      required: true
    },
    allStudies: {
      type: Array,
      required: true
    }
  },
  watch: {
    countries: {
      deep: true,
      handler(countriesArray) {
        if (!countriesArray) {
          return;
        }
        let self = this;
        self.hasAssignmentError = false;
        self.allSelectedSitesCount = 0;
        countriesArray.forEach(country => {
          const selectedSites = country.sites.filter(site => site.isEnabled).length;
          country.selectedSitesCount = selectedSites;
          country.isSomeSiteSelected = selectedSites != country.sites.length && selectedSites > 0;
          country.isEnabled = selectedSites == country.sites.length;
          self.allSelectedSitesCount = self.allSelectedSitesCount + selectedSites;
        });

        self.isSomeSiteSelected = self.allSitesCount != self.allSelectedSitesCount && self.allSelectedSitesCount != 0;
        self.allSitesSelected = self.allSelectedSitesCount == self.allSitesCount && self.allSelectedSitesCount != 0;

      }
    },
    selectedStudy(study) {
      if (!study) {
        return;
      }
      this.getAllSitesOfStudy();
    },
    userDetails: {
      deep: true,
      handler() {
        this.prepareStudiesOptions();
      }
    },
    allStudies() {
      this.prepareStudiesOptions();
    }
  },
  computed: {
    disableSaveButton() {
      return this.isApiCallInProgress || !this.selectedStudy || !this.allSitesCount;
    },
    studyName() {
      if (!this.selectedStudy) {
        return;
      }
      return this.editingAssignment ? this.selectedStudy.studyName : this.selectedStudy.name;
    }
  },
  mounted() {
    this.prepareStudiesOptions();
  },
  methods: {
    resetValues() {
      this.selectedStudy = null;
      this.countries = null;
      this.allSitesCount = 0;
      this.allSitesSelected = false;
      this.isSomeSiteSelected = false;
      this.allSelectedSitesCount = 0;
      this.hasAssignmentError = false;
      this.isStudyAssociatedToUser = false;
    },
    show(assignedStudy) {
      this.editingAssignment = !!assignedStudy;
      this.selectedStudy = assignedStudy;
      this.$refs.assignStudiesModal.show();
    },
    hide() {
      this.resetValues();
      this.$refs.assignStudiesModal.hide();
    },
    prepareStudiesOptions() {
      if (!this.userDetails.userStudies || !this.userDetails.userStudies.length) {
        //If no studies assigned then no need to filter out studies
        this.assignStudiesOptions = cloneDeep(this.allStudies);
        this.assignStudiesOptions.sort((a, b) => a.name.localeCompare(b.name));
        return;
      }

      //Find out the studies already assigned
      let assignedStudyIDs = this.userDetails.userStudies.map(study => study.studyId);
      this.assignStudiesOptions = this.allStudies.filter(study => !assignedStudyIDs.includes(study.studyId));
      this.assignStudiesOptions.sort((a, b) => a.name.localeCompare(b.name));
    },
    studiesInputHandler(e) {
      this.selectedStudy = e;
    },
    getAllSitesOfStudy() {
      this.isApiCallInProgress = true;
      this.allSitesCount = 0;
      this.countries = null;

      this.$api.getSitesFact(this.selectedStudy.studyId).then(
        response => {
          this.isApiCallInProgress = false;
          this.sites = response.body;
          // Put the sites into their countries with
          const countrySiteMap = [];
          for (const site of this.sites) {
            const { countryList } = site;
            let country;
            country = countryList.find(country => country.countryId == site.primaryCountry);

            if (country) {
              site.countryId = country.countryId;
              this.$set(site, "isEnabled", false);
              if (!countrySiteMap[country.countryId]) {
                countrySiteMap[country.countryId] = {
                  country: country,
                  sites: [],
                  expanded: false,
                  includeFuture: false,
                  isEnabled: false,
                  isSomeSiteSelected: false,
                  selectedSitesCount: 0
                };
              }
              countrySiteMap[country.countryId].sites.push(site);
              this.allSitesCount++;
            }
          } //End For loop

          this.countries = Object.values(countrySiteMap);

          // Sort the countries alphabetically based on country name
          this.countries.sort((a, b) => a.country.countryName.localeCompare(b.country.countryName));

          // Sort the sites within each country by userSiteId
          this.countries.forEach(country => {
            country.sites.sort((a, b) => a.userSiteId.localeCompare(b.userSiteId));
          });

          this.adjustAssignmentInEditCase();
        },
        error => {
          this.isApiCallInProgress = false;
          this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while getting the list of sites");
        }
      );
    },
    toggleAllCountrySite(isChecked) {
      this.allSitesSelected = isChecked;
      this.countries.forEach(countryWithSites => {
        countryWithSites.isEnabled = isChecked;
        countryWithSites.includeFuture = isChecked;
        countryWithSites.sites.forEach(site => (site.isEnabled = isChecked));
      });
    },
    toggleCountrySiteSelection(event, country) {
      const isCountrySelected = event.target.checked;
      country.sites.forEach(site => {
        site.isEnabled = isCountrySelected;
      });

      country.includeFuture = isCountrySelected;
    },
    toggleSiteSelection(country) {
      //This method handles the includeFuture Toggle for the respective site country
      let selectedSites = country.sites.filter(site => site.isEnabled).length;
      country.includeFuture = selectedSites == country.sites.length;
    },
    isAssignmentValid() {
      if (this.allSitesSelected || this.isSomeSiteSelected) {
        this.hasAssignmentError = false;
        return true;
      }

      this.hasAssignmentError = true;
      return false;
    },
    saveAssignments() {
      if (!this.isAssignmentValid()) {
        return;
      }

      if (this.editingAssignment || this.isStudyAssociatedToUser) {
        this.addUpdateAssignments();
      } else {
        this.associateStudy();
      }
    },
    associateStudy() {
      this.isApiCallInProgress = true;
      this.$api.userAssociateFact(this.selectedStudy.studyId, [{ userId: this.userDetails.id }]).then(
        () => {
          this.isStudyAssociatedToUser = true;
          this.isApiCallInProgress = false;
          this.addUpdateAssignments();
        },
        error => {
          this.isApiCallInProgress = false;
          this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while assigning this study to user.");
        }
      );
    },
    addUpdateAssignments() {
      this.isApiCallInProgress = true;
      const assignments = [];
      assignments.push(
        this.createAssignmentObject({
          assignmentType: this.assignmentType.ALL,
          assignmentId: this.selectedStudy.studyId,
          isEnabled: this.allSitesSelected,
          includeFuture: false
        })
      );

      for (const countryWithSites of this.countries) {
        if (countryWithSites.isEnabled || countryWithSites.isSomeSiteSelected) {
          //Assignment type COUNTRY Record
          assignments.push(
            this.createAssignmentObject({
              assignmentType: this.assignmentType.COUNTRY,
              assignmentId: countryWithSites.country.countryId, // Using the countryId as the assignmentId for COUNTRY type
              isEnabled: countryWithSites.isEnabled,
              includeFuture: countryWithSites.includeFuture
            })
          );
        }

        if (!countryWithSites.isEnabled && countryWithSites.isSomeSiteSelected) {
          //Assignment type SITE Record
          for (const site of countryWithSites.sites) {
            if (site.isEnabled) {
              assignments.push(
                this.createAssignmentObject({
                  assignmentType: this.assignmentType.SITE,
                  assignmentId: site.id, // Using the siteId as the assignmentId for SITE type
                  isEnabled: site.isEnabled,
                  includeFuture: false
                })
              );
            }
          }
        }
      } //End for loop

      const pathParam = { userId: this.userDetails.id };
      const queryParam = { studyId: this.selectedStudy.studyId };
      const payLoad = { assignments: assignments };
      this.$api.saveUpdateSiteAssignments(pathParam, queryParam, payLoad).then(
        response => {
          this.isApiCallInProgress = false;
          this.$toastr.success(this.editingAssignment ? `${this.studyName} has been saved successfully.` : `${this.studyName} has been assigned successfully.`);
          this.$emit("assignmentsDone");
          this.hide();
        },
        error => {
          this.isApiCallInProgress = false;
          this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while saving the sites assignments.");
        }
      );
    },
    adjustAssignmentInEditCase() {
      if (!this.editingAssignment) {
        //This is New Assignment Case
        this.toggleAllCountrySite(true);
        return;
      }
      const assignedStudyObj = this.userDetails.userStudies.find(study => study.studyId === this.selectedStudy.studyId);
      const { siteAssignments } = assignedStudyObj;

      if (!siteAssignments || !siteAssignments.length) {
        //This is an exitsing assigned study which was assigned prior to this implementation & no need to make further changes as by deafult all has been selected.
        this.toggleAllCountrySite(true);
        return;
      }

      // Check for Enable SITE assignments
      for (const siteAssignment of siteAssignments.filter(assignment => assignment.assignmentType === this.assignmentType.SITE && assignment.isEnabled)) {
        //Find out the country id for this site assignment
        const assignedSiteIndex = this.sites.findIndex(site => site.id === siteAssignment.assignmentId);
        if (assignedSiteIndex >= 0) {
          //This will give us the index of the country group
          const countryIndex = this.countries.findIndex(countryObj => countryObj.country.countryId === this.sites[assignedSiteIndex].primaryCountry);
          if (countryIndex >= 0) {
            const siteIndex = this.countries[countryIndex].sites.findIndex(site => site.id === siteAssignment.assignmentId);
            this.countries[countryIndex].sites[siteIndex].isEnabled = siteAssignment.isEnabled;
          }
        }
      }

      // Check for enabled COUNTRY assignments
      for (const countryAssignment of siteAssignments.filter(assignment => assignment.assignmentType === this.assignmentType.COUNTRY && assignment.isEnabled)) {
        const countryIndex = this.countries.findIndex(c => c.country.countryId === countryAssignment.assignmentId);
        if (countryIndex >= 0) {
          this.countries[countryIndex].includeFuture = countryAssignment.includeFuture;
          if (this.countries[countryIndex].includeFuture) {
            /** Select all sites for this country **/
            this.countries[countryIndex].isEnabled = true;
            this.countries[countryIndex].sites.forEach(site => (site.isEnabled = true));
          }
        }
      }
    },
    createAssignmentObject(data) {
      let assignmentObj = {
        id: uuidv4(),
        userId: this.userDetails.id,
        studyId: this.selectedStudy.studyId,
        assignmentType: data.assignmentType,
        assignmentId: data.assignmentId,
        isEnabled: data.isEnabled,
        includeFuture: data.includeFuture
      };

      return assignmentObj;
    }
  }
};
</script>
<style lang="scss">
#assign-study-modal {
  @media (min-width: 992px) {
    .modal-dialog {
      min-width: 726px;
      vertical-align: middle;
    }
  }

  .modal-content {
    .modal-header {
      border-bottom: 0;
      color: #4a4a4a;
      padding: 24px;
      font-family: Roboto-Medium;

      h5 {
        font-size: 22px;
      }

      .close {
        color: #666669;
        font-size: 31px;
        opacity: inherit;
        font-size: 0;
        background-image: url("/static/img/cross-icon.svg");
        background-size: cover;
        margin-top: 0;
        margin-right: 0px;
      }
    }

    .modal-body {
      padding: 0;
      font-size: 14px;
      color: #4a4a4a;

      .section {
        padding: 24px;

        .section-title {
          color: #4a4a4a;
          font-family: Roboto-Medium;
          font-size: 16px;
          display: flex;
          align-items: center;
          margin-bottom: 15px;

          &::after {
            content: "";
            flex: 1;
            margin-left: 1rem;
            height: 1px;
            background-color: #d3dfe4;
          }
        }

        .study-label {
          letter-spacing: 0.15px;
          color: #666669;
        }

        #assignments-section {
          .assignment-error {
            color: #de3434;
            margin-bottom: 15px;

            .fa-exclamation-triangle {
              margin-right: 8px;
            }
          }

          .checkboxes-section {
            display: inline-block;

            .checkbox-label {
              position: relative;
              margin-bottom: 0px;
              cursor: pointer;

              .checkbox-text {
                font-family: Roboto-Bold;
                margin-left: 32px;
                vertical-align: middle;
                cursor: pointer;
              }
            }

            .checkbox-label input {
              opacity: 0;
              position: absolute;
              cursor: pointer;
              z-index: 999;
              height: 15px;
              width: 16px;
              top: 0;
              bottom: 0;
              margin: auto 0;
            }

            .input-checkbox {
              position: absolute;
              height: 15px;
              width: 16px;
              background-color: #fff;
              border-radius: 4px;
              border: 1.5px solid #959fa4;
              top: 0;
              bottom: 0;
              margin: auto 0;
            }

            .input-checkbox::after {
              position: absolute;
              content: "";
              top: 12px;
              left: 12px;
              height: 0px;
              width: 0px;
              background-color: transparent;
              border-radius: 4px;
              border: solid #ffffff;
              border-width: 0 3px 3px 0;
              -webkit-transform: rotate(0deg) scale(0);
              -ms-transform: rotate(0deg) scale(0);
              transform: rotate(0deg) scale(0);
              opacity: 1;
            }

            .input-checkbox {
              &.intermidiate {
                background-color: #1e8fe1;
                border-radius: 4px;
                -webkit-transform: rotate(0deg) scale(1);
                -ms-transform: rotate(0deg) scale(1);
                transform: rotate(0deg) scale(1);
                opacity: 1;
                border: 1.5px solid #1e8fe1;

                &::after {
                  -ms-transform: rotate(90deg) scale(0.6);
                  transform: rotate(90deg) scale(0.6);
                  opacity: 1;
                  left: 0;
                  top: -4px;
                  width: 13px;
                  height: 14px;
                  border: solid #fff;
                  border-width: 0 3px 0px 0;
                  background-color: transparent;
                  border-radius: 0;
                }
              }
            }

            .checkbox-label input:checked ~ .input-checkbox {
              background-color: #1e8fe1;
              border-radius: 4px;
              -webkit-transform: rotate(0deg) scale(1);
              -ms-transform: rotate(0deg) scale(1);
              transform: rotate(0deg) scale(1);
              opacity: 1;
              border: 1.5px solid #1e8fe1;
            }

            .checkbox-label input:checked ~ .input-checkbox:after {
              -ms-transform: rotate(45deg) scale(0.8);
              transform: rotate(45deg) scale(0.8);
              opacity: 1;
              left: 4px;
              top: -2px;
              width: 6px;
              height: 13px;
              border: solid #fff;
              border-width: 0 2px 2px 0;
              background-color: transparent;
              border-radius: 0;
            }

            .switch {
              position: relative;
              width: 38px;
              height: 23px;
              top: 3px;
              margin: 0;

              input {
                display: none;
              }

              /* The slider */
              .slider {
                position: absolute;
                cursor: pointer;
                top: 0;
                left: 0;
                right: 0;
                bottom: -1px;
                border: 2px solid #ccc;
                -webkit-transition: 0.4s;
                transition: 0.4s;

                &:before {
                  position: absolute;
                  content: "";
                  height: 16px;
                  width: 16px;
                  left: 2px;
                  bottom: 2px;
                  background-color: #ccc;
                  -webkit-transition: 0.4s;
                  transition: 0.4s;
                }

                /* Rounded sliders */
                &.round {
                  border-radius: 34px;

                  &:before {
                    border-radius: 50%;
                  }
                }
              }

              input:checked + .slider {
                border: 2px solid #48bf7a;
              }

              input:checked + .slider:before {
                content: "";
                background-color: #48bf7a;
              }

              input:focus + .slider {
                box-shadow: 0 0 1px #fff;
              }

              input:checked + .slider:before {
                position: absolute;
                color: #2ecc71;
                text-align: center;
                font-size: 20px;
                -webkit-transform: translateX(14px);
                -ms-transform: translateX(14px);
                transform: translateX(14px);
              }
            }
          }

          #assignments {
            border-radius: 8px;
            border: solid 1px #d3dfe4;

            .title {
              padding: 16px;
              color: #666669;
              font-family: Roboto-Bold;
              text-transform: capitalize;
              border-bottom: solid 1px #d3dfe4;

              span {
                vertical-align: middle;
              }

              .count {
                margin-left: 8px;
                padding: 2px 8px;
                border-radius: 6px;
                background-color: rgba(76, 140, 228, 0.2);
                color: #4c8ce4;
                font-size: 12px;
                text-align: center;
                text-transform: lowercase;
              }
            }

            .assign-county-site {
              position: relative;
              min-height: 356px;
              max-height: 356px;
              background-color: #f5f8fa;
              border-radius: 8px;
              padding: 8px;
              overflow-y: auto;

              scroll-behavior: smooth;
              //Scrollbar CSS for Mozilla
              scrollbar-color: #abb4b9 white;
              /* thumb and track color */
              scrollbar-width: thin;

              //Below is the scrollbar css for Chrome
              &::-webkit-scrollbar {
                width: 6px;
                background-color: #ffffff;
              }

              &::-webkit-scrollbar-thumb {
                background-color: #abb4b9;
                border-radius: 20px;
              }

              &::-webkit-scrollbar-track {
                background-color: #ffffff;
              }

              .loading {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                font-size: 40px;

                .fa-spin {
                  color: #2181be;
                }
              }

              .country-group {
                border: solid 1px #d3dfe4;
                margin-bottom: 8px;
                background-color: #fff;
                border-radius: 5px;

                &:last-of-type {
                  margin-bottom: 0;
                }

                .country-header {
                  display: flex;
                  flex-direction: row;
                  flex-wrap: nowrap;
                  justify-content: space-between;
                  height: 48px;
                  align-items: center;
                  padding-left: 16px;
                  padding-right: 16px;
                  border-radius: 5px;
                  border-top-left-radius: 5px;
                  border-top-right-radius: 5px;

                  &:hover {
                    background-color: rgba(176, 186, 201, 0.15);
                  }

                  .checkboxes-section {
                    .disabled {
                      opacity: 0.3;
                      cursor: no-drop;
                      pointer-events: none;
                    }

                    .checkbox-label {
                      vertical-align: text-bottom;

                      .checkbox-text {
                        font-family: Roboto-Medium;
                      }
                    }
                  }
                }

                .title {
                  font-family: Roboto-Medium;
                  border: 0;
                  padding: 0;
                }

                .fa {
                  font-size: 21px;
                  cursor: pointer;
                  margin-left: 41px;
                  vertical-align: text-bottom;
                  border-radius: 4px;
                  padding: 1px 5px;
                  color: #666669;

                  &:hover {
                    background-color: rgba(176, 186, 201, 0.15);
                  }
                }

                .site-details {
                  padding: 12px 48px 12px 48px;

                  &:hover {
                    background-color: rgba(176, 186, 201, 0.15);
                  }

                  .checkboxes-section {
                    .checkbox-label {
                      .checkbox-text {
                        font-family: Roboto-Regular;
                      }
                    }
                  }
                }
              }
            }
          }
        }

        .col-5 {
          flex: 0 0 42.49%;
          max-width: 42.49%;
        }
      }

      .footer {
        padding: 24px;
        padding-top: 11px;
        text-align: right;

        button {
          font-size: 14px;

          &.btn-primary {
            background-color: #4c8ce4;
            border-color: #4c8ce4;
            border-radius: 4px;
            font-family: Roboto-Bold;
            padding: 8px 33px;

            &:hover {
              background-color: #34537d;
              border-color: #34537d;
            }

            &:disabled {
              opacity: 0.4;
            }

            &:focus {
              box-shadow: none;
            }
          }

          &.btn-cancel {
            padding: 8px 33px;
            color: #4a4a4a;
            background: none;
            border: 0;
            font-family: Roboto-Medium;
            margin-right: 16px;

            &:hover {
              color: #4a4a4a;
              background: none;
              border: none;
              outline: none;
              box-shadow: none;
            }

            &:active {
              background: none;
              border: none;
            }

            &:focus {
              box-shadow: none;
            }
          }
        }
      }
    }
  }
}
</style>
